// pages/Contact.jsx
import { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  IconButton, 
  Snackbar, 
  Alert,
  Chip,
  alpha,
  Stack,
  Divider,
} from '@mui/material';
import { 
  WhatsApp, 
  Email, 
  Phone, 
  LocationOn, 
  Send, 
  Diamond,
  AccessTime,
  Verified,
  Security,
  Public,
  SupportAgent,
  Schedule,
  CheckCircle,
  Navigation,
  Business,
  Language,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Premium animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

// Abstract Background Component
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatSlow} 25s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatMedium} 30s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatFast} 20s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatSlow} 22s ease-in-out infinite reverse`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '200px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
        transform: 'rotate(25deg)',
        animation: `${shimmerBackground} 4s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '25%',
        right: '15%',
        width: '300px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        transform: 'rotate(-15deg)',
        animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: '150px',
        height: '150px',
        border: '1px solid rgba(212,175,55,0.08)',
        borderRadius: '50%',
        animation: `${floatMedium} 18s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        right: '25%',
        width: '100px',
        height: '100px',
        border: '1px solid rgba(0,150,255,0.1)',
        borderRadius: '50%',
        animation: `${floatFast} 15s ease-in-out infinite reverse`,
      }}
    />
    
    {[...Array(20)].map((_, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${2 + Math.random() * 4}px`,
          height: `${2 + Math.random() * 4}px`,
          background: `rgba(212, 175, 55, ${0.1 + Math.random() * 0.3})`,
          borderRadius: '50%',
          animation: `${floatFast} ${15 + Math.random() * 20}s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
    ))}
    
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', subject: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      setSnackbar({ 
        open: true, 
        message: 'Thank you for reaching out. A Pentillion representative will contact you within 24 hours.', 
        severity: 'success' 
      });
      setFormData({ name: '', email: '', phone: '', message: '', subject: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactMethods = [
    { icon: <Phone />, label: 'Phone', value: '+27 10 123 4567', detail: 'Mon-Fri: 9am - 6pm SAST', action: 'tel:+27101234567' },
    { icon: <Email />, label: 'Email', value: 'info@pentillion.com', detail: 'Response within 24 hours', action: 'mailto:info@pentillion.com' },
    { icon: <WhatsApp />, label: 'WhatsApp', value: '+27 83 123 4567', detail: 'Instant messaging support', action: 'https://wa.me/27831234567' },
    { icon: <LocationOn />, label: 'HQ Address', value: 'Melrose Arch, Johannesburg', detail: 'South Africa', action: null },
  ];

  const globalOffices = [
    { city: 'Johannesburg', country: 'South Africa', flag: '🇿🇦', address: 'Melrose Arch, 2196' },
    { city: 'Toronto', country: 'Canada', flag: '🇨🇦', address: 'Bay Street, M5H 2Y2' },
    { city: 'Auckland', country: 'New Zealand', flag: '🇳🇿', address: 'Queen Street, 1010' },
  ];

  const inquiryTypes = [
    'General Inquiry',
    'Sales & Trading',
    'Minting Services',
    'Distribution & Logistics',
    'Investment Advisory',
    'Partnership Opportunities',
  ];

  const officeHours = [
    'Monday - Friday: 9:00 AM - 6:00 PM SAST',
    'Saturday: 10:00 AM - 2:00 PM SAST',
    'Sunday: Closed',
  ];

  const stats = [
    { label: 'Response Time', value: '< 24h', icon: <Schedule />, description: 'Average response time' },
    { label: 'Client Satisfaction', value: '98%', icon: <CheckCircle />, description: 'Satisfaction rate' },
    { label: 'Global Offices', value: '3', icon: <Public />, description: 'Worldwide locations' },
    { label: 'Support Team', value: '24/7', icon: <SupportAgent />, description: 'Dedicated support' },
  ];

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Contact Background Image */}
        <Box
          sx={{
            minHeight: '45vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: 'url("/images/contact.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 6, md: 8 } }}>
            <Stack spacing={2.5}>
              <Chip
                label="CONTACT US"
                icon={<Diamond sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.2),
                  color: '#D4AF37',
                  width: 'fit-content',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  backdropFilter: 'blur(5px)',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                Connect With Our Experts
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#D4AF37',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  fontWeight: 600,
                }}
              >
                Global Support, Local Presence
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.85),
                  maxWidth: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Connect with our precious metals experts for personalized assistance and institutional-grade solutions
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Stats Section */}
        <Container maxWidth="xl" sx={{ py: 6 }}>
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    animation: `${fadeInUp} 0.6s ease-out ${index * 0.1}s forwards`,
                    opacity: 0,
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      textAlign: 'center',
                      py: 3,
                      px: 2,
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        borderColor: alpha('#D4AF37', 0.4),
                        boxShadow: `0 12px 28px ${alpha('#D4AF37', 0.15)}`,
                      },
                    }}
                  >
                    <Box sx={{ color: '#D4AF37', fontSize: 42, mb: 1 }}>{stat.icon}</Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#0a0a2a', fontSize: { xs: 32, md: 36 } }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#0a0a2a', mt: 0.5 }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                      {stat.description}
                    </Typography>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={6}>
            {/* Left Column - Contact Info */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  animation: `${fadeInLeft} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                  opacity: 0,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#FFFFFF',
                    mb: 1,
                    fontSize: { xs: 28, md: 32 },
                  }}
                >
                  Get in Touch
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), mb: 4 }}>
                  Our team is ready to assist you with any inquiries
                </Typography>

                <Card
                  elevation={0}
                  sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: 3,
                    mb: 3,
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    {contactMethods.map((method, index) => (
                      <Box key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              bgcolor: alpha('#D4AF37', 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#D4AF37',
                              flexShrink: 0,
                            }}
                          >
                            {method.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontWeight: 600, display: 'block' }}>
                              {method.label}
                            </Typography>
                            {method.action ? (
                              <Button 
                                href={method.action} 
                                target={method.label === 'WhatsApp' ? '_blank' : '_self'}
                                sx={{ 
                                  p: 0, 
                                  minWidth: 'auto', 
                                  display: 'block',
                                  textAlign: 'left',
                                  '&:hover': { bgcolor: 'transparent' }
                                }}
                              >
                                <Typography variant="body1" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                                  {method.value}
                                </Typography>
                              </Button>
                            ) : (
                              <Typography variant="body1" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                                {method.value}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>
                              {method.detail}
                            </Typography>
                          </Box>
                        </Box>
                        {index < contactMethods.length - 1 && (
                          <Divider sx={{ my: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>

                {/* Office Hours Card */}
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: 3,
                    mb: 3,
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                      <AccessTime sx={{ color: '#D4AF37', fontSize: 28 }} />
                      <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
                        Office Hours
                      </Typography>
                    </Box>
                    <Stack spacing={1.5}>
                      {officeHours.map((hour, index) => (
                        <Typography key={index} variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>
                          {hour}
                        </Typography>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Global Offices Card */}
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                      <Language sx={{ color: '#D4AF37', fontSize: 28 }} />
                      <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
                        Global Offices
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      {globalOffices.map((office, index) => (
                        <Box key={index}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>{office.flag}</Typography>
                            <Box>
                              <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                                {office.city}, {office.country}
                              </Typography>
                              <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                                {office.address}
                              </Typography>
                            </Box>
                          </Box>
                          {index < globalOffices.length - 1 && (
                            <Divider sx={{ mt: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Trust Badges */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    icon={<Verified />}
                    label="24/7 Support"
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.15),
                      color: '#D4AF37',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    icon={<Security />}
                    label="Secure Communication"
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.15),
                      color: '#D4AF37',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Contact Form */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  animation: `${fadeInRight} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s forwards`,
                  opacity: 0,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#FFFFFF',
                    mb: 1,
                    fontSize: { xs: 28, md: 32 },
                  }}
                >
                  Send an Inquiry
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), mb: 4 }}>
                  Fill out the form below and a specialist will contact you promptly
                </Typography>

                <Card
                  elevation={0}
                  sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#F8F9FC',
                                '& fieldset': { borderColor: alpha('#D4AF37', 0.2) },
                                '&:hover fieldset': { borderColor: alpha('#D4AF37', 0.4) },
                                '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                              },
                              '& .MuiInputLabel-root': { color: alpha('#0a0a2a', 0.6) },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                              '& .MuiOutlinedInput-input': { color: '#0a0a2a' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#F8F9FC',
                                '& fieldset': { borderColor: alpha('#D4AF37', 0.2) },
                                '&:hover fieldset': { borderColor: alpha('#D4AF37', 0.4) },
                                '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                              },
                              '& .MuiInputLabel-root': { color: alpha('#0a0a2a', 0.6) },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                              '& .MuiOutlinedInput-input': { color: '#0a0a2a' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone (Optional)"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#F8F9FC',
                                '& fieldset': { borderColor: alpha('#D4AF37', 0.2) },
                                '&:hover fieldset': { borderColor: alpha('#D4AF37', 0.4) },
                                '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                              },
                              '& .MuiInputLabel-root': { color: alpha('#0a0a2a', 0.6) },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                              '& .MuiOutlinedInput-input': { color: '#0a0a2a' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            select
                            label="Inquiry Type"
                            value={formData.subject}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            SelectProps={{ native: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#F8F9FC',
                                '& fieldset': { borderColor: alpha('#D4AF37', 0.2) },
                                '&:hover fieldset': { borderColor: alpha('#D4AF37', 0.4) },
                                '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                              },
                              '& .MuiInputLabel-root': { color: alpha('#0a0a2a', 0.6) },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                              '& .MuiOutlinedInput-input': { color: '#0a0a2a' },
                            }}
                          >
                            <option value="">Select inquiry type</option>
                            {inquiryTypes.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Message"
                            multiline
                            rows={5}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#F8F9FC',
                                '& fieldset': { borderColor: alpha('#D4AF37', 0.2) },
                                '&:hover fieldset': { borderColor: alpha('#D4AF37', 0.4) },
                                '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                              },
                              '& .MuiInputLabel-root': { color: alpha('#0a0a2a', 0.6) },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                              '& .MuiOutlinedInput-input': { color: '#0a0a2a' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            endIcon={isSubmitting ? null : <Send />}
                            fullWidth
                            disabled={isSubmitting}
                            sx={{
                              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                              color: '#0a0a2a',
                              fontWeight: 700,
                              py: 1.5,
                              fontSize: '0.9rem',
                              letterSpacing: 1,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                                transform: 'translateY(-2px)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </CardContent>
                </Card>

                <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.4), display: 'block', textAlign: 'center', mt: 2 }}>
                  By submitting this form, you agree to our privacy policy. Your information is secure and confidential.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Global Presence Section - Premium */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 8 }, mt: 4, position: 'relative' }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Box sx={{ animation: `${fadeInUp} 0.6s ease-out forwards`, opacity: 0 }}>
              <Diamond sx={{ fontSize: 56, color: '#D4AF37', mb: 2, animation: `${pulse} 2s ease-in-out infinite` }} />
              <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                Global Presence, Local Support
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 4, maxWidth: 600, mx: 'auto' }}>
                With offices strategically located across three continents, we provide localized support 
                for our global clientele with 24/7 responsiveness.
              </Typography>
              <Stack 
                direction="row" 
                spacing={2} 
                justifyContent="center" 
                flexWrap="wrap" 
                sx={{ gap: 2 }}
              >
                <Chip 
                  label="🇿🇦 South Africa" 
                  sx={{ 
                    bgcolor: alpha('#D4AF37', 0.1), 
                    color: '#D4AF37',
                    fontWeight: 600,
                    px: 1,
                  }} 
                />
                <Chip 
                  label="🇨🇦 Canada" 
                  sx={{ 
                    bgcolor: alpha('#D4AF37', 0.1), 
                    color: '#D4AF37',
                    fontWeight: 600,
                  }} 
                />
                <Chip 
                  label="🇳🇿 New Zealand" 
                  sx={{ 
                    bgcolor: alpha('#D4AF37', 0.1), 
                    color: '#D4AF37',
                    fontWeight: 600,
                  }} 
                />
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* CTA Section - Direct Contact */}
        <Box sx={{ py: 8 }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Card
              elevation={0}
              sx={{
                p: 5,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                borderRadius: 3,
              }}
            >
              <SupportAgent sx={{ fontSize: 56, color: '#D4AF37', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                Need Immediate Assistance?
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3 }}>
                Our dedicated support team is available 24/7 to assist with urgent inquiries
              </Typography>
              <Stack 
                direction="row" 
                spacing={2} 
                justifyContent="center" 
                flexWrap="wrap" 
                sx={{ gap: 2 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Phone />}
                  href="tel:+27101234567"
                  sx={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                    color: '#0a0a2a',
                    fontWeight: 700,
                    px: 4,
                    py: 1.2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Call Now
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WhatsApp />}
                  href="https://wa.me/27831234567"
                  target="_blank"
                  sx={{
                    borderColor: '#D4AF37',
                    color: '#D4AF37',
                    fontWeight: 700,
                    px: 4,
                    py: 1.2,
                    '&:hover': {
                      borderColor: '#D4AF37',
                      bgcolor: alpha('#D4AF37', 0.05),
                    },
                  }}
                >
                  WhatsApp Us
                </Button>
              </Stack>
            </Card>
          </Container>
        </Box>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity="success"
            icon={<CheckCircle />}
            sx={{
              bgcolor: '#0a0a2a',
              color: '#D4AF37',
              '& .MuiAlert-icon': { color: '#D4AF37' },
              fontWeight: 600,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Contact;